<template>
  <div>
  <!-- Os três campos curtos numa linha, que empilha no telefone; a descrição, longa, embaixo -->
  <v-row>
    <v-col cols="12" md="4">
      <v-text-field
          :label="$tt('workspace-name')" class="required" :model-value="value.title"
          :error-messages="errorMessages.title"
          @update:model-value="onInput('title', $event); validateName($event)"
          @blur="validateName()"
      />
    </v-col>
    <v-col cols="12" md="4">
      <ontos-computed-input
          :label="$tt('alias')"
          class="required unique ontos-code"
          :value="value.alias"
          @input="onInput('alias', $event); validateAlias($event)"
          @blur="validateAlias()"
          :readonly="fixedAlias"
          :manual="manualAlias" @input:manual="manualAlias = $event"
          :source="value.title"
          :compute="$utils.aliasSuggestion"
          :computeOnInput="$utils.incompleteAliasSuggestion"
          :error-messages="errorMessages.alias"
      />
    </v-col>
    <v-col cols="12" md="4">
      <!-- Um só para o ambiente: é ele que diz que dia é "hoje" nas fórmulas, igual para todos -->
      <v-autocomplete :label="$tt('time-zone')" :items="timeZones" :model-value="value.timeZone"
                      :hint="$tt('time-zone-hint')" persistent-hint
                      @update:model-value="onInput('timeZone', $event)"/>
    </v-col>
  </v-row>
  <v-row>
    <v-col cols="12">
      <!-- A apresentação do ambiente, que a página de entrada mostra embaixo do nome. Com as referências
           e as imagens do markdown de um item (pedido do usuário), menos o `{{campo}}`: não há item. O
           ambiente é o dono do texto, com o `_mentions` dele; ao criar um, ainda não há ambiente, e o
           editor fica sem elas -->
      <label class="ontos-field-label">{{ $tt('workspace-description') }}</label>
      <markdown-editor ref="description" initialEditType="wysiwyg" mentions :item="value" @change="onDescription"
                       @showMessage="$emit('showMessage', $event)"/>
    </v-col>
  </v-row>
  </div>
</template>
<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import ComputedInput from "@/components/shared/ComputedInput.vue";
import {t} from "@/i18n";
import {namingError} from "@/utils/fieldValidation";
import {timeZoneOptions} from "@/utils/timeZones";
import MarkdownEditor from "@/components/shared/markdown/MarkdownEditor.vue";

@Component({emits: ['input', 'showMessage'], components: {'ontos-computed-input': ComputedInput, 'markdown-editor': MarkdownEditor}})
class SystemInput extends Vue {
  @Prop({type: Object, required: true}) readonly value!: any
  @Prop({type: Boolean, default: false}) readonly fixedAlias!: boolean
  @Prop({type: Array, default: () => []}) readonly systems!: any[]

  manualAlias = false
  errorMessages = {
    title: "",
    alias: ""
  }
  //Exposto ao template: a lista é do navegador, e não muda enquanto a tela está aberta
  timeZones = timeZoneOptions()

  /**
   * A descrição entra depois de montar, sem mexer no cursor — o mesmo cuidado do campo markdown do
   * item (ver `InstancePropertyInput.mounted`): com `initialValue` o editor posiciona o cursor no
   * construtor, e um documento que começa com lista não aceita essa seleção.
   */
  mounted() {
    const editor = this.$refs.description as InstanceType<typeof MarkdownEditor> | undefined
    if (editor && this.value.description) editor.setMarkdown(this.value.description, false)
  }

  /** Vazia é não ter descrição: sai do corpo, e na edição vira `null`, que apaga a gravada. */
  onDescription() {
    const editor = this.$refs.description as InstanceType<typeof MarkdownEditor>
    this.onInput('description', editor.getMarkdown() || undefined)
  }

  onInput(prop: string, value: any) {
    const changed: { [k: string]: any } = {}
    changed[prop] = value
    this.$emit('input', {...this.value, ...changed})
  }

  /** Confere o título ou o alias entre os outros ambientes — ver `namingError`. */
  checkNaming(prop: 'title' | 'alias', text: string) {
    this.errorMessages[prop] = namingError(text, prop, this.systems, this.fixedAlias ? this.value.alias : undefined)
    return !this.errorMessages[prop]
  }

  validateName(text: string = this.value.title) {
    return this.checkNaming('title', text)
  }

  validateAlias(text: string = this.value.alias) {
    return this.checkNaming('alias', text)
  }

  /** Todos os campos, e não até o primeiro que falha: o usuário vê de uma vez o que corrigir. */
  validateModel() {
    return (Object.keys(this.errorMessages) as Array<'title' | 'alias'>)
        .map(prop => this.checkNaming(prop, this.value[prop]))
        .every(valid => valid)
  }

  /**
   * O servidor recusou o valor por já existir — a tela pode estar desatualizada. O mesmo nome do
   * formulário de coleção; era um `registerError(tipo, campo)` chamado só com 'unique'.
   */
  notUnique(prop: 'title' | 'alias') {
    this.errorMessages[prop] = t('validation.already-used', {value: this.value[prop]})
  }
}


export default toNative(SystemInput)
</script>
